import React, { Component } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { FormicaSelectBoxItem } from '../components/custom/FormicaSearchSelectBox';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import CollectionNames from '../constant/CollectionNames';
import CustomLoading from '../components/custom/CustomLoading';
import ItemToolsMenu from '../components/ItemToolsMenu';
import DataType from '../constant/DataType';
import { RootState } from '../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { changePageFilter, clearPage } from '../redux/actions/PageActions';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import { CaseGraphFieldState, GraphState, TransactionState } from '../redux/reducers/GraphReducer';
import {
  changeGraphFields,
  changeGraphLoading,
  changeInformationCoordinates,
  changeInformationVisible,
  changeLoading,
  changeNodeLimit,
  changeResponse,
  changeSelectedFields,
  changeSelectedNode,
  changeSelectedTransaction,
  changeTransactions,
  changeUpdate,
  clearGraph,
} from '../redux/actions/GraphActions';
import GraphSettingsComponent from '../components/graph/GraphSettingsComponent';
import GraphFooter from '../components/graph/GraphFooter';
import GraphExecutorComponent from '../components/graph/GraphExecutorComponent';
import FilterUtil from '../util/FilterUtil';
import moment from 'moment';
import { CustomFilterField } from '../domain/ui/FilterDomains';
import { GraphExportJob } from '../domain/job/JobDomain';
import MessageUtil from '../util/MessageUtil';
import JobService from '../service/JobService';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import { cloneDeep } from 'lodash';
import CaseService from '../service/CaseService';
import SuspiciousActivityReportSidebarComponent from '../components/sar/SuspiciousActivityReportSidebarComponent';
import { TransactionData } from '../domain/stream/transaction/TransactionDomains';
import { ComponentState } from '../redux/actions/ComponentActions';
import { User } from '../domain/rbac/RbacDomains';
import eq = query.eq;
import Filter = query.Filter;
import ConditionalFilter = query.ConditionalFilter;
import LanguageUtil from '../util/LanguageUtil';

class GraphContainer extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private configurationSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);
  private caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);

  public render() {
    const {
      graph: { loading, response, caseGraphFields },
      component: { module },
      user,
    } = this.props;
    const { filter, reportVisible, data, reportLoading } = this.state;

    return (
      <div className={'flex flex-col w-full h-full'}>
        <Breadcrumb />
        <GraphSettingsComponent filter={filter} onChangeFilter={this.onChangeFilter} />
        <div style={{ boxShadow: '0px 2px 12px 0px #0000000A' }} className={'flex w-full px-5 border-b-[1px] border-[#E0E4E8]'}>
          <ItemToolsMenu
            onChangeFilter={this.onChangeFilter}
            importVisible={false}
            exportVisibleAlways
            exportVisible={true}
            exportFunc={this.onExport}
            reportVisible={response?.nodes != undefined && response.nodes.length > 0 && user.activeRiskType == 'compliance' && module.includes('aml')}
            reportFunc={this.onReport}
            reportLoading={reportLoading}
          />
        </div>
        <div className={'flex flex-col w-full h-full items-start justify-center'}>
          {loading ? (
            <CustomLoading />
          ) : (
            <>
              <GraphExecutorComponent key={'graph-container-executer'} filter={filter} onChangeFilter={this.onChangeFilter} />
              <GraphFooter />
            </>
          )}
        </div>
        <SuspiciousActivityReportSidebarComponent
          visible={reportVisible}
          data={data}
          sarConfiguration={caseGraphFields?.suspiciousActivityReport}
          onClose={() => this.setState({ reportVisible: false })}
        />
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
    this.setRouterParams();
  }

  public async componentWillUnmount() {
    const { clearGraph } = this.props;
    clearGraph();
    this.props.clearPage();
    await this.stopTracker();
  }

  public async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const {
      graph: { selectedTransaction, caseGraphFields },
    } = this.props;
    if (prevProps.graph.selectedTransaction != selectedTransaction) {
      await this.resubscribeTracker();
    }
    if (prevProps.graph.caseGraphFields != caseGraphFields) {
      this.setSelectedGraphFields();
    }
  }

  private tracker = async () => {
    const {
      graph: { selectedTransaction },
      changeTransactions,
      changeGraphFields,
      changeLoading,
    } = this.props;
    if (this.transactionSubscription?.ready() && this.configurationSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).select('id', 'name', 'variables').build();
      const caseConfigurationQuery: query.Query = query.QueryBuilder.select()
        .from(CollectionNames.CASE_SETTINGS)
        .where(eq('dataSource', selectedTransaction))
        .select(
          'id',
          'graphConfiguration',
          'transactionCurrencyField',
          'transactionAmountField',
          'suspiciousActivityReport',
          'graphDefaultFilter',
          'graphDefaultFilterRequired',
        )
        .build();

      const caseGraphFields: CaseGraphFieldState | undefined = await this.repository.find(caseConfigurationQuery);
      const transactions: TransactionState[] | undefined = await this.repository.findMany(transactionQuery);

      changeTransactions(transactions);
      changeGraphFields(caseGraphFields);
      changeLoading(false);

      await this.updateFilterFields();
      this.setSelectedGraphFields();
    }
  };

  private startTracker = async () => {
    const {
      graph: { selectedTransaction },
    } = this.props;

    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    const caseConfigurationQuery = query.QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).where(eq('dataSource', selectedTransaction));

    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.configurationSubscription = subscribe.Formica.subscribe(CollectionNames.CASE_SETTINGS, caseConfigurationQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription, this.configurationSubscription]);
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.configurationSubscription?.stop();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation.onStop(this.startTracker);
      await this.stopTracker();
    }
  };
  private setSelectedGraphFields = (): any => {
    const {
      graph: { caseGraphFields },
    } = this.props;
    const selectedItems: FormicaSelectBoxItem[] = [];
    caseGraphFields?.graphConfiguration.forEach((graphField) => {
      if (graphField.enabled) {
        selectedItems.push({ label: graphField.label, value: graphField.field, icon: graphField.color, dataType: graphField.dataType });
      }
    });
    this.props.changeSelectedFields(selectedItems);
  };

  private updateFilterFields = async () => {
    const { graph } = this.props;
    await FilterUtil.getCaseFields(graph.selectedTransaction, true);
  };

  private onChangeFilter = (filter: Filter) => {
    this.setState({ filter });
  };

  private setRouterParams = (): void => {
    const { router, changeSelectedTransaction, changeLoading } = this.props;
    const { transaction, filter } = router.params;
    if (transaction && filter) {
      try {
        const parsedFilter = JSON.parse(filter);
        this.setState({ isPropsLoaded: true, filter: parsedFilter });
        changeLoading(false);
        this.setFilter(parsedFilter);
      } catch (e) {
        console.error(e);
        changeLoading(true);
      }
      changeSelectedTransaction(transaction);
    }
  };

  private setFilter = (filter: ConditionalFilter): void => {
    const { pageFilter, changePageFilter } = this.props;
    const pageFilters = pageFilter.filter.fields;
    filter.filters.forEach((f: any) => {
      const _filter: CustomFilterField = {
        field: f.key,
        enabled: true,
        label: f.key.split('.')[1],
        search: {
          type: 'LITERAL',
          value: {
            properties: {
              dataType: DataType.STRING,
            },
            operator: f.type,
            value: f.value,
          },
        },
      };
      pageFilters.push(_filter);
    });
    pageFilter.filter.fields = pageFilters;
    changePageFilter(pageFilter);
  };

  private onExport = async () => {
    const {
      graph: { response, selectedTransaction, selectedFields },
    } = this.props;
    const _response = cloneDeep(response);
    delete _response.links;
    _response.nodes.map((node: any) => {
      delete node.gfx;
      delete node.x;
      delete node.y;
      delete node.vx;
      delete node.vy;
      delete node.nodeColor;
      delete node.color;
    });

    try {
      const job: GraphExportJob = {
        type: 'GRAPH_EXPORT',
        timestamp: moment().valueOf(),
        status: 'PENDING',
        graphResponse: _response,
        transactionId: selectedTransaction,
        groups: selectedFields?.map((item: FormicaSelectBoxItem) => ({ label: item.label, field: item.value })),
        filter: this.state.filter,
        extension: 'csv',
        path: '',
      };
      await this.jobService.schedule(job);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.export', defaultMessage: 'Export' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.success.exportScheduled', defaultMessage: 'Export job scheduled' })}</div>,
      });
    } catch (e) {
      console.error(e);
    }
  };

  private onReport = async () => {
    const {
      graph: { response, selectedTransaction },
    } = this.props;
    this.setState({ reportLoading: true });
    const nodes = response.nodes?.map((node: any) => {
      return { field: node.group, value: node.label };
    });
    const data = await this.caseService.executeSAR(nodes, selectedTransaction);
    this.setState({ data: data, reportVisible: true, reportLoading: false });
  };
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  pageFilter: state.page.filter,
  graph: state.graph as GraphState,
  component: state.component as ComponentState,
  user: state.auth.user as User,
});
const mapDispatchToProps = {
  changePageFilter,
  changeGraphFields,
  changeTransactions,
  changeGraphLoading,
  changeLoading,
  changeSelectedTransaction,
  changeNodeLimit,
  changeResponse,
  changeSelectedNode,
  changeInformationVisible,
  changeInformationCoordinates,
  changeSelectedFields,
  clearGraph,
  clearPage,
  changeUpdate,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  filter: any = {
    filterType: 'ConditionalFilter',
    type: 'and',
    filters: [],
  };
  isPropsLoaded: boolean = false;
  reportVisible: boolean = false;
  data: TransactionData[] = [];
  reportLoading: boolean = false;
}

export default withFormica(connector(GraphContainer));
